import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Popup } from 'react-vant'
import Ripple from '@/components/Ripple'
import CustomButton from '@/components/CustomButton'
import { formatThousands } from '@/utils'

export interface Currency {
  icon: string
  name: string
  description: string
  symbol: string
  key?: string
  balance?: string
}

export const DEFAULT_CURRENCIES: Currency[] = [
  {
    icon: '/images/home/usdt.png',
    name: 'USDT',
    description: 'Tether USD',
    symbol: 'USDT'
  },
  {
    icon: '/images/home/usdc.png',
    name: 'USDC',
    description: 'USD Coin',
    symbol: 'USDC'
  },
  {
    icon: '/images/home/usd1.png',
    name: 'USD1',
    description: 'USD1',
    symbol: 'USD1'
  }
]

interface CurrencySelectPopupProps {
  visible: boolean
  onClose: () => void
  onSelect: (currency: Currency) => void
  currencies?: Currency[]
  title?: string
  /** 当前选中的币种标识（key 或 symbol），用于弹窗打开时高亮当前选中项 */
  selectedValue?: string
}

export default React.memo(function CurrencySelectPopup({
  visible,
  onClose,
  onSelect,
  currencies = DEFAULT_CURRENCIES,
  title,
  selectedValue
}: CurrencySelectPopupProps) {
  const { t } = useTranslation()
  const [tempSelected, setTempSelected] = useState<string>('')

  const getCurrencyId = useCallback((currency: Currency) => {
    return currency.key || currency.symbol
  }, [])

  useEffect(() => {
    if (visible) {
      if (selectedValue) {
        setTempSelected(selectedValue)
      } else if (currencies.length > 0) {
        setTempSelected(getCurrencyId(currencies[0]))
      }
    }
  }, [visible, selectedValue, currencies, getCurrencyId])

  const handleConfirm = useCallback(() => {
    const selected = currencies.find(c => getCurrencyId(c) === tempSelected)
    if (selected) {
      onSelect(selected)
      onClose()
    }
  }, [tempSelected, currencies, getCurrencyId, onSelect, onClose])

  return (
    <Popup
      visible={visible}
      position="bottom"
      round
      closeable
      onClose={onClose}
      closeIcon={<img src="/icon/close.svg" alt="" />}
    >
      <div className="p-5">
        <div className="text-left text-base font-semibold text-black mb-4">
          {title || t('选择支付币种')}
        </div>
        <div className="space-y-2">
          {currencies.map((currency) => {
            const id = getCurrencyId(currency)
            const isSelected = tempSelected === id

            return (
              <Ripple key={id} className="rounded-2xl">
                <div
                  className={`flex items-center border border-solid border-transparent justify-between p-4 rounded-2xl cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-[#F0F2FF] border border-solid !border-[var(--primary-color)]'
                      : 'bg-[#F7F8FA]'
                  }`}
                  onClick={() => setTempSelected(id)}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={currency.icon}
                      alt={currency.name}
                      className="w-9 h-9 rounded-full"
                    />
                    <div>
                      <div className="text-sm font-medium text-[#1D2129] mb-1">{currency.name}</div>
                      <div className="text-xs text-[#6A7187]">
                        {currency.balance !== undefined
                          ? `${t('余额')}: ${formatThousands(Number(currency.balance), 2, true)}`
                          : currency.description
                        }
                      </div>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5">
                      <img src="/images/card-v2/check-pay.svg" alt="check-pay" />
                    </div>
                  )}
                </div>
              </Ripple>
            )
          })}
        </div>
        <div className="mt-4">
          <CustomButton onClick={handleConfirm}>
            {t('确定')}
          </CustomButton>
        </div>
      </div>
    </Popup>
  )
})
